// =============================================================================
// deliveryOrdersSync.js — outbound courier hand-off proof, dual-written.
//
// WHAT THIS IS FOR. When a courier collects an Uber Eats order, staff photograph
// the bag with the order number showing. That photo is the proof it left the
// shop, and if a customer says "it never arrived" it is the whole argument.
// Today it lives in a Google Sheet with the picture on Google Drive. The Supabase
// tables for it were built in PR #65 and have never had a single row written to
// them. This writes them.
//
// WHAT IT DOES NOT DO:
//   * It does not replace the Sheet write or the Drive upload. Both happen first,
//     unchanged, and this runs after they have already succeeded. If this whole
//     file were deleted the till would behave exactly as it does today.
//   * It does not touch INBOUND deliveries — stock arriving at the kitchen — which
//     is a separate system, a separate table, and already migrated (#69).
//   * It cannot slow down or break the hand-off. It runs after the on-screen
//     confirmation, swallows every error, and never throws.
//
// APPEAR-ONCE IS THE POINT. The 12–14 May triplicate-orders bug lived in this
// exact path — a silent sheet-routing fallback made every order read back three
// times. The row is keyed on the till's own per-order token, so submitting the
// same hand-off twice is one row, proven by query rather than by hope.
//
// PHOTO-LESS ORDERS ARE FLAGGED, NOT HIDDEN. If the Storage upload fails the row
// is still written, carrying the Drive links — an order with no proof at all is
// exactly the thing that must be findable (delivery_orders_without_photo(), 0092).
//
// …EXCEPT WHEN THIS WRITE IS THE RECORD (review of Lane BM, 13 Sep 2026). A caller
// that has stopped writing to Google passes `requireAllPhotos: true`. Then a
// hand-off whose photos did not ALL reach the bucket is NOT written: the result is
// { ok:false, reason:"photo-short", photos:{attempted, landed} } and the door is
// never called, so the till keeps the form and the staff member taps Confirm
// again — the photos that landed are keyed no-ops, only the missing one is sent
// again. Writing the row anyway would lose the picture for good: the door is
// on-conflict-do-nothing (a retry can never attach it), a partial shortfall is
// flagged nowhere (0092 flags only rows with NO photo), and the only copy of a
// fresh camera shot is the tablet's in-memory data URL. Google's own path has
// always failed the whole submit on a bad upload; a drop host keeps that rule.
// The default (shadow path) is unchanged.
//
// THE ONE-LINE KILL: set DELIVERY_ORDERS_SYNC_ENABLED to false, below.
//
// Inert unless dualRunEnabled() — the same single dual-run switch every other
// module uses. On staging, a preview build or localhost it does nothing at all.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled, siteIdForKey, shadow, ensurePass } from "./stockSync.js";
import { courierHandoffRow, outboundPhotoKey } from "./deliveryOrderRow.js";

const BUCKET = "delivery-photos";

// ---- the one-line kill ------------------------------------------------------
// false = stop recording hand-offs in Supabase. The Sheet write, the Drive
// upload and the on-screen list are all untouched either way.
export const DELIVERY_ORDERS_SYNC_ENABLED = true;

const londonToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });

function base64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---- one more go, for a connection that dropped -----------------------------
// Kiosk tablets sit on shopping-centre wifi and drop out for a second or two.
// Anything that fails to REACH Supabase gets exactly one more attempt, ~1.5s
// later, while the staff pass is still valid.
//
// Safe only because both halves are keyed: an upload re-uses the same object key
// (so a retry of one that actually landed is an "already exists" no-op), and the
// row re-uses the same idempotency token (so a retry that turns out to be a
// duplicate writes nothing). Without those keys this would be the thing that
// created duplicate orders — the exact bug of 12–14 May — rather than the thing
// that stopped losing one.
//
// One retry, not a stored queue. A persistent offline buffer would have to replay
// the hand-off later, under whoever is logged in at that point, which is the one
// thing a proof-of-hand-off record must not get wrong.
const RETRY_AFTER_MS = 1500;
const looksLikeNetwork = (e) =>
  /failed to fetch|networkerror|network request failed|load failed|timeout|econn|fetch failed/i
    .test(String(e?.message || e || ""));

async function withOneRetry(label, fn) {
  try {
    const res = (await fn()) || {};
    if (!res.error || !looksLikeNetwork(res.error)) return res;
    console.warn(`[delivery-orders] ${label} couldn't reach Supabase — one more go`);
  } catch (e) {
    if (!looksLikeNetwork(e)) return { error: e };
    console.warn(`[delivery-orders] ${label} threw on the network — one more go`);
  }
  await new Promise((r) => setTimeout(r, RETRY_AFTER_MS));
  try { return (await fn()) || {}; } catch (e) { return { error: e }; }
}

// ---- the door, and why there is one ----------------------------------------
// Between 14 and 19 Aug 2026 every hand-off this module tried to record was
// refused. Production's request log says how, on two real collections:
//
//     POST /storage/.../delivery-photos/outbound/...   200   <- the photo landed
//     POST /rest/v1/delivery_orders                    400
//          proxy_status: "PostgREST; error=PGRST204"
//
// PGRST204 is "I don't recognise one of the columns you named". It never reached
// the table, so it was never permissions, RLS, the pass or the shop lookup. The
// API layer keeps a CACHED picture of the table, and that cache was behind.
//
// A post that names fourteen columns has fourteen chances to be refused that
// way. A call to one function with one argument has none — which is why the
// activity log, stock and the checklist all already write through a door and
// have never had this failure. This was the last raw table write on the till.
//
// NO FALLBACK ANY MORE (Lane BM, 13 Sep 2026). This module used to keep a direct
// table write in reserve for the case where the door had not been applied yet.
// Migration 1144 made the table's read policy Command-Centre-admins-only, and a
// direct `on conflict (idem_key)` insert needs read rights to check the key — so
// that reserve path is now REFUSED by the database (proven on staging, 42501).
// Keeping it would have turned "door missing" into a second silent failure: the
// exact shape that hid every hand-off for five days in August. So a missing door
// is now LOUD instead — a console.error and an { ok:false, reason:"door-missing" }
// result — and nothing is written anywhere. The door (0600) has been on production
// since 19 Aug 2026; this branch should never run for real.
const DOOR = "record_courier_handoff";
const doorNotThere = (e) =>
  e?.code === "PGRST202" || /could not find the function/i.test(String(e?.message || ""));

// Best-effort upload of one photo. Never throws. `upsert: false` so re-sending the
// same hand-off is a harmless no-op on a key that already exists rather than a
// second copy of the same picture.
//
// Returns the key ONLY if the bytes are actually in the bucket. That ordering is
// the whole safety property of this module: the row is written afterwards and can
// only ever claim photos that really landed, so a hand-off can never point at a
// photo that doesn't exist — which would look proven and fail when it mattered.
async function uploadPhoto(key, base64, mime) {
  const { error } = await withOneRetry("photo upload", () =>
    supabase.storage.from(BUCKET)
      .upload(key, base64ToBytes(base64), { contentType: mime || "image/jpeg", upsert: false }));
  if (error && !/exists|duplicate/i.test(error.message || "")) {
    console.warn("[delivery-orders] photo upload failed:", error.message || error);
    return null;
  }
  return key;   // already-exists counts as landed — the bytes are in the bucket
}

// One call per confirmed courier hand-off, fired AFTER the sheet write succeeded.
//
// `photos` is the same array the till already built for the Drive upload — each
// { dataUrl, mime, filename, capturedAt, gpsLat, gpsLng, exifPresent, source }.
// `legacyUrls` are the Drive URLs that upload just returned, in the same order.
// `clientToken` is the till's own per-order token — the thing that makes this
// appear exactly once.
//
// The raw PIN is used only to mint the pass and never reaches a row or a path.
// Never throws, never rejects.
// `pass` is normally left out — the PIN mints one here. It exists so the tests
// can drive the real module without a live login service.
// `requireAllPhotos` (default false) — see "EXCEPT WHEN THIS WRITE IS THE RECORD"
// in the header: true = a photo-short hand-off is refused BEFORE the door.
export async function recordCourierHandoff({
  siteKey, orderNo, platform, staffPin, clientToken,
  photos = [], legacyUrls = [], note = "", day = londonToday(), pass = null,
  requireAllPhotos = false,
} = {}) {
  try {
    if (!DELIVERY_ORDERS_SYNC_ENABLED) return { ok: true, skipped: "switched off" };
    if (!dualRunEnabled()) return { ok: true, skipped: "dual-run off" };
    if (!siteKey || !orderNo) return { ok: false, error: "missing shop or order number" };
    // No token means no appear-once promise, and an unkeyed row in THIS table is
    // exactly the bug we're here to prevent. Refuse rather than write a duplicate.
    if (!clientToken) return { ok: false, error: "no client token — refusing to write an unkeyed order" };

    const verified = pass || await ensurePass(staffPin, siteKey);
    if (!verified?.ok) { console.warn("[delivery-orders] no pass — hand-off shadow skipped"); return { ok: false, error: "no pass" }; }
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return { ok: false, error: "no site id for " + siteKey };

    // Photos first, so the row knows what actually landed. Sequential rather than
    // parallel: it runs after the staff member has already been told the hand-off
    // saved, so there is nothing to be quick for, and a till on shopping-centre
    // wifi does better with one request at a time.
    const paths = [];
    let attempted = 0;   // photos that had bytes to send — reported back against `paths` below
    for (let i = 0; i < photos.length; i++) {
      const p = photos[i] || {};
      const b64 = String(p.dataUrl || "").split(",")[1] || "";
      if (!b64) continue;
      attempted++;
      const key = outboundPhotoKey({ siteKey, day, orderNo, idx: i, uniq: clientToken, mime: p.mime });
      const landed = await uploadPhoto(key, b64, p.mime);
      if (landed) paths.push(landed);
    }

    // When this write IS the record, a short set of photos is refused here — no
    // row, no door call — so a retry (same keys, same token) can complete it.
    if (requireAllPhotos && paths.length < attempted) {
      console.warn("[delivery-orders] " + (attempted - paths.length) + " of " + attempted +
        " photo(s) for order " + orderNo + " did not reach the bucket — the hand-off was NOT written (all photos are required on this host); retry with the same order");
      return { ok: false, reason: "photo-short", photos: { attempted, landed: paths.length } };
    }

    const row = courierHandoffRow({
      siteId, siteKey, orderNo, platform, clientToken,
      photoPaths: paths, legacyUrls, note, primary: photos[0] || {},
    });
    if (!row) return { ok: false, error: "could not shape the row" };

    // ON CONFLICT (idem_key) DO NOTHING, whichever way it goes in. The same
    // hand-off submitted twice — a double-tap, a retried network call, a till
    // that came back from sleep — is one row. That guarantee lives in a unique
    // constraint, not in either of these two code paths.
    const label = "courier hand-off " + orderNo;

    // The door, and ONLY the door. Same row, handed over whole instead of column
    // by column. (The direct-table reserve write that used to follow this is gone
    // — see the header: after 1144 the database refuses it, so it could only ever
    // have failed quietly.)
    const viaDoor = await withOneRetry("hand-off door", () => supabase.rpc(DOOR, { p_handoff: row }));
    if (doorNotThere(viaDoor.error)) {
      // This is the condition that used to fall through to a silent table write.
      // It must be impossible to miss: a hand-off was NOT recorded in Supabase.
      console.error(
        "[delivery-orders] the hand-off door record_courier_handoff (migration 0600) is MISSING from this project's API — " +
        "the courier hand-off for order " + orderNo + " was NOT recorded in Supabase. Apply 0600 (and reload the API schema) before relying on this write."
      );
      return { ok: false, reason: "door-missing", error: viaDoor.error };
    }
    const res = await shadow(label, async () => viaDoor);
    // How many of the photos the till handed over actually reached the bucket —
    // reported, never hidden. A caller for which this write IS the record (a host
    // that has stopped writing to Google) needs this to tell the staff member when
    // the proof is short: the door's on-conflict-do-nothing means a retry can never
    // attach photos to a row that has already landed without them.
    return { ...res, photos: { attempted, landed: paths.length } };
  } catch (e) {
    console.warn("[delivery-orders] recordCourierHandoff threw:", e?.message || e);
    return { ok: false, error: e };
  }
}
